import { Schema } from "effect";
import {
  isClaudeResumeCompactionNeverAnswer,
  isClaudeResumeCompactionQuestionKey,
} from "@taskin-code/shared/claudeCompaction";

export const CLAUDE_RESUME_COMPACTION_MINUTES = 70;
export const CLAUDE_RESUME_COMPACTION_TOKENS = 100_000;
/**
 * Threads whose resume prompt this browser should stay quiet about. It is a
 * list and not a flag on purpose: the "never ask again" answer lives in the
 * thread's activities, which every member of the team reads, so mirroring it
 * into a single global flag let one person's answer on one thread silence the
 * banner for everyone else, on every Claude thread, with no way back.
 */
export const RESUME_COMPACTION_DISMISSED_STORAGE_KEY =
  "taskin:resume-compaction-dismissed:claudeAgent:threads";
export const RESUME_COMPACTION_DISMISSED_LIMIT = 200;
export const DismissedResumeCompactionThreads = Schema.Array(Schema.String);
export const NO_DISMISSED_RESUME_COMPACTION_THREADS: ReadonlyArray<string> = [];

/** Append `threadId`, keeping the list bounded and the reference stable when
    the thread is already there (so the caller does not write on every render). */
export function rememberDismissedResumeCompactionThread(
  dismissed: ReadonlyArray<string>,
  threadId: string,
): ReadonlyArray<string> {
  if (dismissed.includes(threadId)) return dismissed;
  return [...dismissed, threadId].slice(-RESUME_COMPACTION_DISMISSED_LIMIT);
}

export const RESUME_COMPACTION_PREVIEW_STORAGE_KEY = "taskin:debug:resume-compaction";
export const RESUME_COMPACTION_PREVIEW_SEARCH_PARAM = "previewResumeCompaction";
export const RESUME_COMPACTION_PREVIEW_TOKENS = 275_123;

export function isResumeCompactionPreviewEnabled(input?: {
  readonly storage?: Pick<Storage, "getItem"> | null;
  readonly search?: string | null;
}): boolean {
  const search = input?.search ?? (typeof window === "undefined" ? null : window.location.search);
  if (search) {
    try {
      if (new URLSearchParams(search).get(RESUME_COMPACTION_PREVIEW_SEARCH_PARAM) === "1") {
        return true;
      }
    } catch {
      // Ignore malformed query strings and fall through to storage.
    }
  }

  const storage =
    input?.storage !== undefined
      ? input.storage
      : typeof window === "undefined"
        ? null
        : window.localStorage;
  try {
    return storage?.getItem(RESUME_COMPACTION_PREVIEW_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

export function previewResumeCompactionWindow(now: string): {
  readonly usedTokens: number;
  readonly updatedAt: string;
} {
  const nowMs = Date.parse(now);
  const updatedAt = Number.isFinite(nowMs)
    ? new Date(nowMs - (CLAUDE_RESUME_COMPACTION_MINUTES + 10) * 60_000).toISOString()
    : now;
  return {
    usedTokens: RESUME_COMPACTION_PREVIEW_TOKENS,
    updatedAt,
  };
}

export function hasDismissedResumeCompaction(
  activities: ReadonlyArray<{ readonly kind: string; readonly payload: unknown }>,
): boolean {
  return activities.some((activity) => {
    if (activity.kind !== "user-input.resolved") return false;
    const payload = activity.payload;
    if (!payload || typeof payload !== "object") return false;
    const answers = (payload as { readonly answers?: unknown }).answers;
    if (!answers || typeof answers !== "object" || Array.isArray(answers)) return false;

    return Object.entries(answers).some(
      ([question, answer]) =>
        isClaudeResumeCompactionNeverAnswer(answer) &&
        isClaudeResumeCompactionQuestionKey(question),
    );
  });
}

export function shouldOfferResumeCompaction(input: {
  readonly provider: string | null | undefined;
  readonly usedTokens: number | null | undefined;
  readonly updatedAt: string | null | undefined;
  readonly now: string;
}): boolean {
  if (
    input.provider !== "claudeAgent" ||
    (input.usedTokens ?? 0) < CLAUDE_RESUME_COMPACTION_TOKENS
  ) {
    return false;
  }

  const updatedAt = Date.parse(input.updatedAt ?? "");
  const now = Date.parse(input.now);
  return (
    Number.isFinite(updatedAt) &&
    Number.isFinite(now) &&
    now - updatedAt >= CLAUDE_RESUME_COMPACTION_MINUTES * 60_000
  );
}
